/**
 * Command history that survives a reload (`M9/E7`).
 *
 * Kept in this browser's `localStorage`, one list per signed-in user and one
 * for signed out, so two people sharing a browser never recall each other's
 * commands. Newest first, as ArrowUp walks it. Answers to a prompt — a sign-in
 * code, a key — never reach here: the shell routes them before history.
 *
 * Every read and write is guarded. Storage that throws (Safari private mode)
 * or holds something that is not a list of strings leaves the terminal working
 * with an empty history, never refusing to start.
 */

export const HISTORY_LIMIT = 500;

export const historyKey = (userId: string | undefined) => `atc.history.${userId ?? "signed-out"}`;

export function loadHistory(storage: Storage | undefined, key: string): string[] {
  try {
    const raw = storage?.getItem(key);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((c): c is string => typeof c === "string").slice(0, HISTORY_LIMIT);
  } catch {
    return [];
  }
}

export function saveHistory(storage: Storage | undefined, key: string, history: readonly string[]) {
  try {
    if (history.length === 0) storage?.removeItem(key);
    else storage?.setItem(key, JSON.stringify(history.slice(0, HISTORY_LIMIT)));
  } catch {
    // A full or blocked store costs the history, not the terminal.
  }
}

/** The command added newest-first; the same command twice in a row is kept once, as bash's ignoredups. */
export function pushHistory(history: readonly string[], command: string): string[] {
  return history[0] === command ? [...history] : [command, ...history].slice(0, HISTORY_LIMIT);
}

export const browserStorage = (): Storage | undefined => {
  try {
    return typeof window === "undefined" ? undefined : window.localStorage;
  } catch {
    return undefined;
  }
};
